import React, { useState, useEffect } from 'react';
import CardMCFilter from './CardMCFilter';
import { Button, Icon } from 'semantic-ui-react';
import * as XLSX from 'xlsx';
import CardMCVegaChart from './components/CardMCVegaChart';
import { VisualizationSpec } from 'vega-embed';

type CardMCOldVSNewProps = {
  // Add props if needed
  controller: any;
  runSequence: any;
  MeChartSequence: any;
}

// Component: CardMCOldVSNew
// Purpose: Compares "Old" (Returning) vs "New" patients.
const CardMCOldVSNew = (props: CardMCOldVSNewProps) => {
  console.log('CardMCOldVSNew props: ', props);
  // State for filtering
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(1);
  const [endYear, setEndYear] = useState(new Date().getFullYear());
  const [endMonth, setEndMonth] = useState(12);

  const thaiMonths = [
    'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
  ];

  // State for View Mode: 'overview' (Pie) or 'monthly' (Bar)
  const [viewMode, setViewMode] = useState<'overview' | 'monthly'>('overview');

  // Transpose state
  const [isTransposed, setIsTransposed] = useState(false);

  // State for chart data
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Actions
  const onSearch = () => { fetchData(); };
  const onPrint = () => { window.print(); };

  const onDownload = () => {
    if (!chartData || chartData.length === 0) {
      alert('No data to download');
      return;
    }

    let dataToExport = chartData.map(item => ({
      Month: item.month_label,
      "Old Patients": item.old_count,
      "New Patients": item.new_count
    }));

    if (isTransposed) {
      const headers = Object.keys(dataToExport[0]);
      const transposedData = [
        headers,
        ...dataToExport.map(row => headers.map(header => (row as any)[header]))
      ];
      const pivotedData = transposedData[0].map((_, colIndex) => transposedData.map(row => row[colIndex]));
      const ws = XLSX.utils.aoa_to_sheet(pivotedData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "OldVsNew");
      XLSX.writeFile(wb, `Old_vs_New_Patients_Transposed_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } else {
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "OldVsNew");
      XLSX.writeFile(wb, `Old_vs_New_Patients_${new Date().toISOString().slice(0, 10)}.xlsx`);
    }
  };

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      const startDateStr = `${year}-${String(month).padStart(2, '0')}-01 00:00:00`;
      const lastDay = new Date(year, endMonth, 0).getDate();
      const endDateStr = `${year}-${String(endMonth).padStart(2, '0')}-${lastDay} 23:59:59`;
      const startYearStr = `${year}-01-01 00:00:00`;
      const endYearStr = `${year}-12-31 23:59:59`;

      if (props.MeChartSequence?.sequenceIndex === "Action") {
        const res = await props.runSequence({
          sequence: "MeChart", action: "QuerySQL",
          chart: "new-vs-old-patient",
          startDate: viewMode === "overview" ? startYearStr : startDateStr,
          endDate: viewMode === "overview" ? endYearStr : endDateStr
        })

        console.log('res: ', res);


        if (Array.isArray(res) &&  res?.length > 0 && res?.[0]?.items && Array.isArray(res?.[0]?.items)) {
          // Map month_index to Thai label
          const mapped = res?.[0]?.items.map((d: any) => ({
            ...d,
            month_label: thaiMonths[parseInt(d.month_index) - 1]
          }));
          setChartData(mapped);
        } else {
          setChartData([]);
        }
      }
      setLoading(false);
      
      const response = await fetch(`http://localhost:3002/api/new-vs-old-patient?startDate=${startDateStr}&endDate=${endDateStr}`);
      const data = await response.json();
      console.log('SQL data: ', data);

    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- Vega Data Transformation ---

  // 1. Prepare "Tidy Data" for Vega
  const getVegaData = () => {
    const data: any[] = [];
    chartData.forEach(item => {
      data.push({
        category: item.month_label,
        monthIndex: parseInt(item.month_index),
        type: 'ผู้รับบริการเก่า',
        value: parseInt(item.old_count || 0),
        color: '#F48FB1'
      });
      data.push({
        category: item.month_label,
        monthIndex: parseInt(item.month_index),
        type: 'ผู้รับบริการใหม่',
        value: parseInt(item.new_count || 0),
        color: '#FCE4EC'
      });
    });
    return data;
  };

  // 2. Prepare Pie Data (Aggregated)
  const getPieData = () => {
    let oldTotal = 0;
    let newTotal = 0;
    chartData.forEach(item => {
      oldTotal += parseInt(item.old_count || 0);
      newTotal += parseInt(item.new_count || 0);
    });
    return [
      { type: 'ผู้รับบริการเก่า', value: oldTotal, color: '#F48FB1' },
      { type: 'ผู้รับบริการใหม่', value: newTotal, color: '#FCE4EC' }
    ];
  };


  // --- Vega Specs ---

  const getSpec = (): VisualizationSpec => {
    if (viewMode === 'overview') {
      return {
        width: "container",
        height: "container",
        autosize: { type: "fit", contains: "padding" },
        data: { values: getPieData() },
        encoding: {
          theta: { field: "value", type: "quantitative", stack: true },
          color: {
            field: "type",
            type: "nominal",
            scale: { range: ["#F48FB1", "#FCE4EC"] },
            legend: { title: "Patient Type" }
          },
          order: { field: "value", sort: "descending" }
        },
        layer: [
          {
            mark: { type: "arc", outerRadius: 120, tooltip: true }
          },
          {
            mark: { type: "text", radius: 140 },
            encoding: {
              text: { field: "value", type: "quantitative", format: "," },
              color: { value: "black" }
            }
          }
        ],
        view: { stroke: null }
      };
    } else {
      // Bar Chart (Monthly)
      return {
        width: "container",
        height: "container",
        autosize: { type: "fit", contains: "padding" },
        data: { values: getVegaData() },
        mark: { type: "bar", tooltip: true },
        encoding: {
          [isTransposed ? 'y' : 'x']: {
            field: "category",
            type: "ordinal",
            axis: { title: "เดือน (Month)", labelAngle: isTransposed ? 0 : -45 },
            sort: thaiMonths
          },
          [isTransposed ? 'x' : 'y']: {
            field: "value",
            type: "quantitative",
            axis: { title: "Count" }
          },
          xOffset: isTransposed ? undefined : { field: "type" },
          yOffset: isTransposed ? { field: "type" } : undefined,

          color: {
            field: "type",
            type: "nominal",
            scale: { range: ["#F48FB1", "#FCE4EC"] },
            legend: { title: null }
          },
          tooltip: [
            { field: "category", title: "Month" },
            { field: "type", title: "Type" },
            { field: "value", title: "Count", format: "," }
          ]
        }
      };
    }
  };

  return (
    <div id="card-mc-old-vs-new">
      {/* Row 1: Toggles */}
      <div className="controls-container">
        {/* View Mode Toggles */}
        <Button.Group>
          <Button
            active={viewMode === 'overview'}
            style={viewMode === 'overview' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
            onClick={() => setViewMode('overview')}
          >
            จำนวนผู้รับบริการ เก่า / ใหม่ (Overview)
          </Button>
          <Button
            active={viewMode === 'monthly'}
            style={viewMode === 'monthly' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
            onClick={() => setViewMode('monthly')}
          >
            จำนวนผู้รับบริการ เก่า / ใหม่ แต่ละเดือน
          </Button>
        </Button.Group>

        {/* Chart Type Toggles (Right Side) */}
        <Button.Group>
          <Button
            active={viewMode === 'overview'}
            disabled={viewMode === 'monthly'}
            style={viewMode === 'overview' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
            icon
          >
            <Icon name='pie graph' /> Pie
          </Button>
          <Button
            active={viewMode === 'monthly'}
            disabled={viewMode === 'overview'}
            style={viewMode === 'monthly' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
            icon
          >
            <Icon name='chart bar' /> Bar
          </Button>
        </Button.Group>
      </div>

      {/* Row 2: Filter and Download */}
      <div className="filter-container">
        <CardMCFilter
          mode={viewMode === 'overview' ? 'yearOnly' : 'oneYearMonth'}
          year={year}
          month={month}
          endYear={endYear}
          endMonth={endMonth}
          onYearChange={setYear}
          onMonthChange={setMonth}
          onEndYearChange={setEndYear}
          onEndMonthChange={setEndMonth}
          onSearch={fetchData}
          onPrint={onPrint}
        >
          <Button style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }} size='small' icon labelPosition='left' onClick={onDownload}>
            <Icon name='download' />
            Download .xlsx
          </Button>
          <Button
            style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }}
            size='small'
            icon
            labelPosition='left'
            onClick={() => setIsTransposed(!isTransposed)}
            disabled={viewMode === 'overview'}
            title="Transpose"
          >
            <Icon name='exchange' />
            Transpose
          </Button>
        </CardMCFilter>
      </div>

      {/* Row 4: Chart */}
      <div className="chart-wrapper">
        {loading ? (
          <div>Loading...</div>
        ) : (
          <CardMCVegaChart spec={getSpec()} actions={false} />
        )}
      </div>
    </div>
  );
};

export default CardMCOldVSNew;
